import {
  CompositionSpecificCategoryProductType,
  TranslatedAppearanceGroup,
} from 'commons/composition/compositionCategoryHelper.ts';
import { HotspotKey } from '@spreadgroup/common-partner-lib/states';
import { reactive, Ref, toRefs } from 'vue';
import { useCompositionStore } from 'commons/stores/composition.ts';

export type BrandName = CompositionSpecificCategoryProductType['brandName'];

export type ProductTypeFilters = {
  activeAppearanceGroups: TranslatedAppearanceGroup[];
  activeHotspots: HotspotKey[];
  activeBrands: BrandName[];
  activeTargetGroups: string[];
  activeSizeGroups: string[];
};

export type ProductTypeFiltersRefs = {
  [K in keyof ProductTypeFilters]: Ref<ProductTypeFilters[K]>;
};

export const useProductTypeFilters = (): ProductTypeFiltersRefs => {
  const filters = reactive<ProductTypeFilters>({
    activeAppearanceGroups: [],
    activeHotspots: [],
    activeBrands: [],
    activeTargetGroups: [],
    activeSizeGroups: [],
  });

  return toRefs(filters);
};

const checkProductTypeForSellable = (
  productType: CompositionSpecificCategoryProductType,
) => {
  const compositionSellables = useCompositionStore().current?.sellables || [];

  return !compositionSellables.some(
    (sellable) => sellable.productTypeId === productType.id,
  );
};

const checkProductTypeForAppearanceGroup = (
  productType: CompositionSpecificCategoryProductType,
  { activeAppearanceGroups }: Partial<ProductTypeFilters>,
): boolean => {
  if (!activeAppearanceGroups?.length) return true;

  return activeAppearanceGroups.some((group) =>
    productType.appearanceGroups.includes(group.id),
  );
};

const checkProductTypeForHotspot = (
  productType: CompositionSpecificCategoryProductType,
  { activeHotspots }: Partial<ProductTypeFilters>,
): boolean => {
  if (!activeHotspots?.length) return true;

  return activeHotspots.some((hotspot) =>
    productType.hotspots?.includes(hotspot),
  );
};

const checkProductTypeBrands = (
  productType: CompositionSpecificCategoryProductType,
  { activeBrands }: Partial<ProductTypeFilters>,
): boolean => {
  if (!activeBrands?.length) return true;

  return activeBrands.includes(productType.brandName);
};

const checkProductTypeTargetGroups = (
  productType: CompositionSpecificCategoryProductType,
  { activeTargetGroups }: Partial<ProductTypeFilters>,
): boolean => {
  if (!activeTargetGroups?.length) return true;
  return activeTargetGroups.some((targetGroup: string) =>
    productType.labels?.some((label) => label.id === targetGroup),
  );
};

const checkProductTypeSizeGroups = (
  productType: CompositionSpecificCategoryProductType,
  { activeSizeGroups }: Partial<ProductTypeFilters>,
): boolean => {
  if (!activeSizeGroups?.length) return true;

  return productType.sizeGroups.some((sizeGroup: string) =>
    activeSizeGroups.includes(sizeGroup),
  );
};

export const applyFilters = (
  productTypes: CompositionSpecificCategoryProductType[],
  filters: Partial<ProductTypeFilters>,
): CompositionSpecificCategoryProductType[] => {
  return productTypes.filter((productType) => {
    const checks = [
      checkProductTypeForSellable(productType),
      !filters.activeAppearanceGroups?.length ||
        checkProductTypeForAppearanceGroup(productType, filters),
      !filters.activeHotspots ||
        checkProductTypeForHotspot(productType, filters),
      !filters.activeBrands || checkProductTypeBrands(productType, filters),
      !filters.activeTargetGroups ||
        checkProductTypeTargetGroups(productType, filters),
      !filters.activeSizeGroups ||
        checkProductTypeSizeGroups(productType, filters),
    ];
    return checks.every(Boolean);
  });
};
